import { GridColumns } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";
import Table from "../../../reusable-components/Table/Table";
import { FT_CDT } from "../../../utils/constants/filter-tables-constants";
import { CandidateDTO } from "../../../models/recruiting/candidate/candidate-dto";
import { CandidateApiService } from "../../../services/recruiting/candidate/candidate-api.service";
import { RecruitersApiService } from "../../../services/recruiting/recruiters/recruiters-api.service";
import { CountriesApiService } from "../../../services/countries/countries-api.service";
import AttachedDocuments from "../../../containers/AttachedDocuments/AttachedDocuments";
import AddNewDocument from "../../../containers/AddNewDocument/AddNewDocument";
import { CandidateSTO } from "../../../models/recruiting/candidate/candidate-sto";
import { DocumentRef } from "../../../models/documents/document-ref";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import { Container, AddButton, SkillsContainer, SkillsBlock, CenteredCell } from "./styles";
import { AvailabilityStatusApiService } from "../../../services/recruiting/candidate/availability-status-api.service/availability-status-api.service";
import { intlFormatDateWithHour } from "../../../utils/time-utils";
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES, SKILLS_CATEGORIES } from "../../../utils/constants/constants";
import { DocumentCategoryRef } from "../../../models/document-categories";
import { DocumentTypeRef } from "../../../models/document-types";
import { useAvailabilityStatusesQuickFilter } from "../../../containers/Quickfilters/AvailabilityQuickfilter/useAvailabilityStatusesQuickFilter";
import { PropsFilter } from "../../../models/api/props-filter/props-filter";
import QuickfiltersContainer, {
  FiltersContainer,
} from "../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { AvailabilityStatusesQuickfilter } from "../../../containers/Quickfilters/AvailabilityQuickfilter/AvailabilityStatusesQuickfilter";
import { defaultRates, RateFilter } from "../../../containers/Quickfilters/PositionRateQuickfilter/RateFilter";
import SkillsDetailsModal from "./SkillsDetailsModal/SkillsDetailsModal";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { SkillCategoryRef } from "../../../models/recruiting/candidate/skills/skill-category/skill-category-ref";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import CommentBubble from "../../../reusable-components/CommentBubble/CommentBubble";
import { Button, Tooltip, Typography, useTheme } from "@mui/material";
import { AddEditCandidateCvStatus } from "./AddEditCandidateCvStatus/AddEditCandidateCvStatus";
import { AddEditSkills } from "./AddEditSkills/AddEditSkills";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import { PositionRef } from "../../../models/recruiting/positions/position-ref";
import { InterviewRef } from "../../../models/recruiting/interviews/interview-ref";
import { ColumnsHeaderTitle } from "../../../reusable-components/Table/styles";
import { RecruitersFilter } from "src/models/recruiting/recruiters";

const Candidates = () => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.candidates";
  const translationFields = `${tRP}.fields`;

  const theme = useTheme();
  const { forceUpdate } = useForceUpdate();

  const isAllowedEditAddDelete = useAuthorization([
    AUTHORIZATIONS.RecruitingAccess.CanOpenPositionsAndAddCreateCandidates,
  ]);

  const [minAndMaxRate, setMinAndMaxRate] = useState<{
    minRate: number;
    maxRate: number | null;
  }>({
    minRate: defaultRates.minRate,
    maxRate: defaultRates.maxRate,
  });

  const setRates = (minRate: number, maxRate: number | null) => {
    setMinAndMaxRate({
      minRate: minRate,
      maxRate: maxRate,
    });
  };

  const { availabilityStatusesQuickFilter, setAvailabilityStatusesQuickFilter } = useAvailabilityStatusesQuickFilter();

  const saveCandidateNoteDocument = async (candidate: CandidateSTO, document: DocumentRef) => {
    const candidateToSave = { ...candidate, notesDocument: document };

    const response = await new CandidateApiService().saveItems([candidateToSave]);

    toastifyResponse(response, "failed");

    return response;
  };

  const saveCandidateFullCVDocument = async (candidate: CandidateSTO, document: DocumentRef) => {
    const candidateToSave = { ...candidate, fullCVDocument: document };

    const response = await new CandidateApiService().saveItems([candidateToSave]);

    toastifyResponse(response, "failed");

    return response;
  };

  const saveCandidateConfCVDocument = async (candidate: CandidateSTO, document: DocumentRef) => {
    const candidateToSave = { ...candidate, confCVDocument: document };

    const response = await new CandidateApiService().saveItems([candidateToSave]);

    toastifyResponse(response, "failed");

    return response;
  };

  const CandidateCodeContainer = (row: CandidateDTO) => {
    return (
      <ConcatenatedCell
        aligned={"left"}
        FirstElement={
          <Grid
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>{row?.candidateCode ?? ""}</span>
            {row?.description && (
              <CommentBubble
                title={`${t(`${translationFields}.description`)}`}
                comment={row?.description}
              />
            )}
          </Grid>
        }
        SecondElement={
          <Grid>
            <Typography
              style={{ color: theme.palette.text.disabled }}
              variant={"body2"}
              title={row?.country?.name ?? ""}
            >
              {row?.country?.code ?? row?.country?.name ?? ""}
            </Typography>
          </Grid>
        }
      />
    );
  };

  const CandidateCodeCellMemoizedRenderer = React.memo(CandidateCodeContainer, (prevProps, nextProps) => {
    return prevProps === nextProps;
  });

  const HiringClientContainer = (row: CandidateDTO) => (
    <ConcatenatedCell
      aligned={"left"}
      FirstElement={
        <Grid>
          <span title={row?.name ?? ""}>{row?.name ?? ""}</span>
        </Grid>
      }
      SecondElement={
        <Grid>
          <Typography
            style={{ color: theme.palette.text.disabled }}
            variant={"body2"}
            title={row?.email ?? ""}
          >
            {row?.email}
          </Typography>
        </Grid>
      }
    />
  );

  const HiringClientCellMemoizedRenderer = React.memo(HiringClientContainer, (prevProps, nextProps) => {
    return prevProps?.name === nextProps?.name && prevProps?.email === nextProps?.email;
  });

  const NotesContainer = (row: CandidateDTO) => (
    <CenteredCell>
      {row?.notesDocument ? (
        <AttachedDocuments
          count={row.notesDocument?.filesCount}
          documentIDs={[row.notesDocument?.id]}
          buttonSize={"2x"}
          handleNotifyParent={forceUpdate}
          documentsNames={[row?.notesDocument?.name]}
          documentsReadOnlyFields={[
            {
              documentID: row.notesDocument?.id,
              readOnlyFields: {
                isLegalEntityRO: true,
              },
            },
          ]}
          hideReadOnlyFields={true}
        />
      ) : (
        <AddNewDocument
          buttonType="plusButton"
          useMiniPlusButton={true}
          relation={{
            relatedEntity: row as CandidateSTO,
            relationHandler: saveCandidateNoteDocument,
          }}
          isWidget
          documentFieldsProps={{
            category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
            type: DOCUMENT_TYPES.EmployeeCV as DocumentTypeRef,
          }}
          handleNotifyParent={forceUpdate}
        />
      )}
    </CenteredCell>
  );

  const NotesCellMemoizedRenderer = React.memo(NotesContainer, (prevProps, nextProps) => {
    return prevProps?.notesDocument === nextProps?.notesDocument;
  });

  const FullCvContainer = (row: CandidateDTO) => (
    <CenteredCell>
      {row?.fullCVDocument ? (
        <AttachedDocuments
          count={row.fullCVDocument?.filesCount}
          documentIDs={[row.fullCVDocument?.id]}
          buttonSize={"2x"}
          handleNotifyParent={forceUpdate}
          documentsNames={[row?.fullCVDocument?.name]}
          documentsReadOnlyFields={[
            {
              documentID: row.fullCVDocument?.id,
              readOnlyFields: {
                isLegalEntityRO: true,
              },
            },
          ]}
          hideReadOnlyFields={true}
        />
      ) : (
        <AddNewDocument
          buttonType="plusButton"
          useMiniPlusButton={true}
          relation={{
            relatedEntity: row as CandidateSTO,
            relationHandler: saveCandidateFullCVDocument,
          }}
          isWidget
          documentFieldsProps={{
            category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
            type: DOCUMENT_TYPES.EmployeeCV as DocumentTypeRef,
          }}
          handleNotifyParent={forceUpdate}
        />
      )}
    </CenteredCell>
  );

  const FullCvCellMemoizedRenderer = React.memo(FullCvContainer, (prevProps, nextProps) => {
    return prevProps?.fullCVDocument === nextProps?.fullCVDocument;
  });

  const ConfCvContainer = (row: CandidateDTO) => (
    <CenteredCell>
      {row?.confCVDocument ? (
        <AttachedDocuments
          count={row.confCVDocument?.filesCount}
          documentIDs={[row.confCVDocument?.id]}
          buttonSize={"2x"}
          handleNotifyParent={forceUpdate}
          documentsNames={[row?.confCVDocument?.name]}
          documentsReadOnlyFields={[
            {
              documentID: (row.fullCVDocument?.id as string) ?? "",
              readOnlyFields: {
                isLegalEntityRO: true,
              },
            },
          ]}
          hideReadOnlyFields={true}
        />
      ) : (
        <AddNewDocument
          buttonType="plusButton"
          useMiniPlusButton={true}
          relation={{
            relatedEntity: row as CandidateSTO,
            relationHandler: saveCandidateConfCVDocument,
          }}
          isWidget
          documentFieldsProps={{
            category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
            type: DOCUMENT_TYPES.EmployeeCV as DocumentTypeRef,
          }}
          handleNotifyParent={forceUpdate}
        />
      )}
    </CenteredCell>
  );

  const ConfCvCellMemoizedRenderer = React.memo(ConfCvContainer, (prevProps, nextProps) => {
    return prevProps?.confCVDocument === nextProps?.confCVDocument;
  });

  const CvSentToClient = (row: CandidateDTO) => (
    <>
      {row?.cVsSentToClientInProgress && row?.cVsSentToClientDeclined && (
        <Container>
          <div>
            {row.cVsSentToClientInProgress.map((cv, idx) => (
              <React.Fragment key={idx}>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsSentToClientInProgress as PositionRef[]) ?? []}
                  currCollectionName={"cVsSentToClientInProgress"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </React.Fragment>
            ))}
          </div>

          <div>
            {row.cVsSentToClientDeclined.map((cv, idx) => (
              <>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsSentToClientDeclined as PositionRef[]) ?? []}
                  currCollectionName={"cVsSentToClientDeclined"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    style={{ color: theme.palette.error.main }}
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </>
            ))}
          </div>
        </Container>
      )}
    </>
  );

  const CvSentToClientCellMemoizedRenderer = React.memo(CvSentToClient, (prevProps, nextProps) => {
    return (
      prevProps?.cVsSentToClientInProgress === nextProps?.cVsSentToClientInProgress &&
      prevProps?.cVsSentToClientDeclined === nextProps?.cVsSentToClientDeclined
    );
  });

  const CvInterviewedByClient = (row: CandidateDTO) => (
    <>
      {row?.cVsInterviewedByClientInProgress && row?.cVsInterviewedByClientDeclined && (
        <Container>
          <div>
            {row.cVsInterviewedByClientInProgress.map((cv, idx) => (
              <React.Fragment key={idx}>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsInterviewedByClientInProgress as PositionRef[]) ?? []}
                  currCollectionName={"cVsInterviewedByClientInProgress"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </React.Fragment>
            ))}
          </div>
          <div>
            {row.cVsInterviewedByClientDeclined.map((cv, idx) => (
              <React.Fragment key={idx}>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsInterviewedByClientDeclined as PositionRef[]) ?? []}
                  currCollectionName={"cVsInterviewedByClientDeclined"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    style={{ color: theme.palette.error.main }}
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </React.Fragment>
            ))}
          </div>
        </Container>
      )}
    </>
  );

  const CvInterviewedByClientCellMemoizedRenderer = React.memo(CvInterviewedByClient, (prevProps, nextProps) => {
    return (
      prevProps?.cVsInterviewedByClientInProgress === nextProps?.cVsInterviewedByClientInProgress &&
      prevProps?.cVsInterviewedByClientDeclined === nextProps?.cVsInterviewedByClientDeclined
    );
  });

  const CvInOffer = (row: CandidateDTO) => (
    <>
      {row?.cVsInOfferInProgress && row?.cVsInOfferDeclined && (
        <Container>
          <div>
            {row.cVsInOfferInProgress.map((cv, idx) => (
              <React.Fragment key={idx}>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsInOfferInProgress as PositionRef[]) ?? []}
                  currCollectionName={"cVsInOfferInProgress"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </React.Fragment>
            ))}
          </div>
          <div>
            {row.cVsInOfferDeclined.map((cv, idx) => (
              <React.Fragment key={idx}>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsInOfferDeclined as PositionRef[]) ?? []}
                  currCollectionName={"cVsInOfferDeclined"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    style={{ color: theme.palette.error.main }}
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </React.Fragment>
            ))}
          </div>
        </Container>
      )}
    </>
  );

  const CvInOfferCellMemoizedRenderer = React.memo(CvInOffer, (prevProps, nextProps) => {
    return (
      prevProps?.cVsInOfferInProgress === nextProps?.cVsInOfferInProgress &&
      prevProps?.cVsInOfferDeclined === nextProps?.cVsInOfferDeclined
    );
  });

  const CvHiredContainer = (row: CandidateDTO) => (
    <>
      {row?.cVsHired && row?.cVsOfferRejected && (
        <Container>
          <div>
            {row.cVsHired.map((cv, idx) => (
              <React.Fragment key={idx}>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsHired as PositionRef[]) ?? []}
                  currCollectionName={"cVsHired"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </React.Fragment>
            ))}
          </div>
          <div>
            {row.cVsOfferRejected.map((cv, idx) => (
              <React.Fragment key={idx}>
                <AddEditCandidateCvStatus
                  effect={"edit"}
                  key={idx}
                  position={cv}
                  item={row}
                  currCollection={(row.cVsOfferRejected as PositionRef[]) ?? []}
                  currCollectionName={"cVsOfferRejected"}
                  currIndex={idx}
                  handleNotifyParent={forceUpdate}
                >
                  <span
                    style={{ color: theme.palette.error.main }}
                    key={cv?.id}
                    title={(cv?.description as string) ?? ""}
                  >
                    {cv?.positionCode}
                  </span>
                </AddEditCandidateCvStatus>
              </React.Fragment>
            ))}
          </div>
        </Container>
      )}
    </>
  );

  const CvHiredCellMemoizedRenderer = React.memo(CvHiredContainer, (prevProps, nextProps) => {
    return prevProps?.cVsHired === nextProps?.cVsHired && prevProps?.cVsOfferRejected === nextProps?.cVsOfferRejected;
  });

  const LanguageContainer = (row: CandidateDTO) => (
    <SkillsBlock>
      <SkillsDetailsModal
        skillCategory={SKILLS_CATEGORIES.language as SkillCategoryRef}
        notifyParent={forceUpdate}
        candidate={row}
      >
        <SkillsContainer title={"Click here to edit and for more information"}>
          {row?.skills
            ?.filter((currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.language.id)
            .map((currSkill) => currSkill?.skill?.name)
            .join(", ")}
        </SkillsContainer>
      </SkillsDetailsModal>

      <AddButton>
        <AddEditSkills
          effect={"add"}
          handleNotifyParent={forceUpdate}
          candidate={row}
          skillCategory={SKILLS_CATEGORIES.language as SkillCategoryRef}
        >
          <Button>
            <FontAwesomeIcon icon={faEllipsisV} />
          </Button>
        </AddEditSkills>
      </AddButton>
    </SkillsBlock>
  );

  const LanguageCellMemoizedRenderer = React.memo(LanguageContainer, (prevProps, nextProps) => {
    return prevProps?.skills === nextProps?.skills;
  });

  const PersonalSkillsContainer = (row: CandidateDTO) => (
    <SkillsBlock>
      <SkillsDetailsModal
        skillCategory={SKILLS_CATEGORIES.personal as SkillCategoryRef}
        notifyParent={forceUpdate}
        candidate={row}
      >
        <SkillsContainer title={"Click here to edit and for more information"}>
          {row?.skills
            ?.filter((currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.personal.id)
            .map((currSkill) => currSkill?.skill?.name)
            .join(", ")}
        </SkillsContainer>
      </SkillsDetailsModal>

      <AddButton>
        <AddEditSkills
          effect={"add"}
          handleNotifyParent={forceUpdate}
          candidate={row}
          skillCategory={SKILLS_CATEGORIES.personal as SkillCategoryRef}
        >
          <Button>
            <FontAwesomeIcon icon={faEllipsisV} />
          </Button>
        </AddEditSkills>
      </AddButton>
    </SkillsBlock>
  );

  const PersonalSkillsCellMemoizedRenderer = React.memo(PersonalSkillsContainer, (prevProps, nextProps) => {
    return prevProps?.skills === nextProps?.skills;
  });

  const TechnicalSkillsContainer = (row: CandidateDTO) => (
    <SkillsBlock>
      <SkillsDetailsModal
        skillCategory={SKILLS_CATEGORIES.technical as SkillCategoryRef}
        notifyParent={forceUpdate}
        candidate={row}
      >
        <SkillsContainer title={"Click here to edit and for more information"}>
          {row?.skills
            ?.filter((currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.technical.id)
            .map((currSkill) => currSkill?.skill?.name)
            .join(", ")}
        </SkillsContainer>
      </SkillsDetailsModal>

      <AddButton>
        <AddEditSkills
          effect={"add"}
          handleNotifyParent={forceUpdate}
          candidate={row}
          skillCategory={SKILLS_CATEGORIES.technical as SkillCategoryRef}
        >
          <Button>
            <FontAwesomeIcon icon={faEllipsisV} />
          </Button>
        </AddEditSkills>
      </AddButton>
    </SkillsBlock>
  );

  const TechnicalSkillsCellMemoizedRenderer = React.memo(TechnicalSkillsContainer, (prevProps, nextProps) => {
    return prevProps?.skills === nextProps?.skills;
  });

  const columns: GridColumns = [
    {
      field: "candidateCode",
      renderCell: ({ row }) => (
        <CandidateCodeCellMemoizedRenderer {...row} />
        // <ConcatenatedCell
        //   aligned={"left"}
        //   FirstElement={
        //     <Grid
        //       sx={{
        //         display: "flex",
        //         flexDirection: "row",
        //         alignItems: "center",
        //         justifyContent: "space-between",
        //       }}
        //     >
        //       <span>{row?.candidateCode ?? ""}</span>
        //       {row?.description && (
        //         <CommentBubble
        //           title={`${t(`${translationFields}.description`)}`}
        //           comment={row?.description}
        //         />
        //       )}
        //     </Grid>
        //   }
        //   SecondElement={
        //     <Grid>
        //       <Typography
        //         style={{ color: theme.palette.text.disabled }}
        //         variant={"body2"}
        //         title={row?.country?.name ?? ""}
        //       >
        //         {row?.country?.code ?? row?.country?.name ?? ""}
        //       </Typography>
        //     </Grid>
        //   }
        // />
      ),
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.code`)} | ${t(`${translationFields}.country`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.code`)} | ${t(
            `${translationFields}.country`
          )}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
    },

    {
      field: "hiringClient",
      renderCell: ({ row }) => <HiringClientCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.name`)} | ${t(`${translationFields}.email`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.name`)} | ${t(
            `${translationFields}.email`
          )}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
    },

    {
      field: "recruiter",
      valueGetter: ({ row }) => row?.recruiter?.recruiterCode,
      headerName: t(`${translationFields}.recruiter`),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      align: "center",
    },

    {
      field: "availability",
      renderCell: ({ row }) => <div title={row?.availability?.name}>{row?.availability?.code}</div>,
      headerName: t(`${translationFields}.availability`),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      align: "center",
    },

    {
      field: "rate",
      valueGetter: ({ row }) => row?.rate,
      headerName: t(`${translationFields}.rate`),
      headerAlign: "center",
      flex: 1,
      align: "center",
      minWidth: 70,
    },

    {
      field: "notes",
      renderCell: ({ row }) => <NotesCellMemoizedRenderer {...row} />,
      headerName: t(`${translationFields}.notes`),
      headerAlign: "center",
      flex: 1,
      minWidth: 50,
      sortable: false,
      filterable: false,
    },

    {
      field: "fullCV",
      renderCell: ({ row }) => <FullCvCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.fullCV`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.fullCV`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 50,
      sortable: false,
      filterable: false,
    },

    {
      field: "confCVDocument",
      renderCell: ({ row }) => <ConfCvCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.confCVDocument`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.confCVDocument`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 50,
      sortable: false,
      filterable: false,
    },

    {
      field: "cVsSentToClient",
      renderCell: ({ row }) => <CvSentToClientCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.cv_sent_to_client`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.cv_sent_to_client`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
    },

    {
      field: "cVsInterviewdByClient",
      renderCell: ({ row }) => <CvInterviewedByClientCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.cv_interviewed_by_client`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.cv_interviewed_by_client`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
    },

    {
      field: "cVsInOffer",
      renderCell: ({ row }) => <CvInOfferCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.cv_in_offer`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.cv_in_offer`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
    },

    {
      field: "cVsHired",
      renderCell: ({ row }) => <CvHiredCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.cv_hired`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.cv_hired`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
    },

    {
      field: "interviews",
      renderCell: ({ row: { interviews } }) => (
        <>
          {interviews.map((interview: InterviewRef, idx: number) => {
            const isLastElement = interviews.length === idx + 1;

            return (
              <span
                key={interview?.id}
                title={intlFormatDateWithHour(interview?.dateInterview as Date)}
                style={{ marginRight: "5px" }}
              >
                {`${interview?.interviewCode} ${isLastElement ? "" : ","}`}
              </span>
            );
          })}
        </>
      ),
      headerName: t(`${translationFields}.interviews`),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
    },

    {
      field: "specialization",
      valueGetter: ({ row }) => row?.specialization,
      headerName: t(`${translationFields}.specialization`),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
    },

    {
      field: "language",
      headerName: "Language skills",
      renderCell: ({ row }) => <LanguageCellMemoizedRenderer {...row} />,

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      sortable: false,
      filterable: false,
    },

    {
      field: "personal",
      headerName: "Personal skills",
      renderCell: ({ row }) => <PersonalSkillsCellMemoizedRenderer {...row} />,

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      sortable: false,
      filterable: false,
    },

    {
      field: "technical",
      headerName: "Technical skills",
      renderCell: ({ row }) => <TechnicalSkillsCellMemoizedRenderer {...row} />,

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      sortable: false,
      filterable: false,
    },
  ];

  const formFields: FormField[] = [
    {
      labelKey: t(`${translationFields}.code`),
      field: "candidateCode",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.country`),
      field: "country",
      fieldType: "dropdown",
      apiService: new CountriesApiService(),
    },
    {
      labelKey: t(`${translationFields}.name`),
      field: "name",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.email`),
      field: "email",
      fieldType: "textfield",
      type: "email",
    },
    {
      labelKey: t(`${translationFields}.recruiter`),
      field: "recruiter",
      fieldType: "dropdown",
      apiService: new RecruitersApiService(),
      propsFilter: {
        isInactive: false,
      } as RecruitersFilter,
    },
    {
      labelKey: t(`${translationFields}.description`),
      field: "description",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.availability`),
      field: "availability",
      fieldType: "dropdown",
      apiService: new AvailabilityStatusApiService(),
    },
    {
      labelKey: t(`${translationFields}.rate`),
      field: "rate",
      fieldType: "textfield",
      type: "number",
    },
    {
      labelKey: t(`${translationFields}.specialization`),
      field: "specialization",
      fieldType: "textfield",
    },
  ];

  const defaultItem: Partial<CandidateDTO> = {
    candidateCode: "",
    country: null,
    name: "",
    email: "",
    recruiter: null,
    description: "",
    availability: null,
    rate: null,
    notesDocument: null,
    fullCVDocument: null,
    confCVDocument: null,
    cVsSentToClientInProgress: [],
    cVsSentToClientDeclined: [],
    cVsInterviewedByClientInProgress: [],
    cVsInterviewedByClientDeclined: [],
    cVsInOfferInProgress: [],
    cVsInOfferDeclined: [],
    cVsHired: [],
    cVsOfferRejected: [],
    interviews: [],
    skills: [],
    specialization: "",
  };

  return (
    <>
      {isAllowedEditAddDelete && (
        <Table
          translationRootPath={tRP}
          columns={columns}
          serviceApi={new CandidateApiService()}
          formFields={formFields}
          defaultItem={defaultItem}
          filter={FT_CDT}
          readOnly={!isAllowedEditAddDelete}
          propsFilter={
            {
              availabilityStatusIds: availabilityStatusesQuickFilter.availabilityStatuses.availabilityStatusesId
                ? [availabilityStatusesQuickFilter.availabilityStatuses.availabilityStatusesId]
                : [],
              minRate: minAndMaxRate.minRate === 0 ? null : minAndMaxRate.minRate,
              maxRate: minAndMaxRate.maxRate === 0 ? null : minAndMaxRate.maxRate,
            } as PropsFilter
          }
          quickFilters={
            <QuickfiltersContainer>
              <FiltersContainer>
                <AvailabilityStatusesQuickfilter
                  quickFilters={availabilityStatusesQuickFilter}
                  handleChangeQuickFilters={setAvailabilityStatusesQuickFilter}
                />
                <RateFilter setRates={setRates} />
              </FiltersContainer>
            </QuickfiltersContainer>
          }
        />
      )}
    </>
  );
};

export default Candidates;
